import { Injectable } from '@angular/core';
import { confirmSignIn, signIn, type SignInInput } from 'aws-amplify/auth';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { confirmSignUp, type ConfirmSignUpInput } from 'aws-amplify/auth';
import { signUp } from 'aws-amplify/auth';
import {
  updateUserAttribute,
  type UpdateUserAttributeOutput,
} from 'aws-amplify/auth';
import { UserRole } from '../enum/user-role';
import {
  confirmUserAttribute,
  type ConfirmUserAttributeInput,
} from 'aws-amplify/auth';
import {
  sendUserAttributeVerificationCode,
  type VerifiableUserAttributeKey,
} from 'aws-amplify/auth';
import { signOut } from 'aws-amplify/auth';
import { resetPassword, type ResetPasswordOutput } from 'aws-amplify/auth';
import {
  confirmResetPassword,
  type ConfirmResetPasswordInput,
} from 'aws-amplify/auth';

@Injectable({
  providedIn: 'root',
})
export class CognitoService {
  async handleSignUp({ username, password, email }: SignUpParameters) {
    //TODO TESTARE
    try {
      const { isSignUpComplete, userId, nextStep } = await signUp({
        username,
        password,
        options: {
          userAttributes: {
            email,
            'custom:group': UserRole.SITE,
          },
        },
      });
      return userId;
    } catch (error) {
      console.log('error signing up:', error);
      throw error;
    }
  }

  async handleSignUpConfirmation({
    //TODO TESTARE
    username,
    confirmationCode,
  }: ConfirmSignUpInput) {
    try {
      const { isSignUpComplete, nextStep } = await confirmSignUp({
        username,
        confirmationCode,
      });
      return { isSignUpComplete: isSignUpComplete, nextStep: nextStep };
    } catch (error) {
      console.log('error confirming sign up', error);
      throw error;
    }
  }

  async handleSignIn({ username, password }: SignInInput) {
    try {
      const { isSignedIn, nextStep } = await signIn({ username, password });

      return {
        isSignedIn: isSignedIn,
        nextStep: nextStep,
      };
    } catch (error) {
      console.log('error signing in', error);
      throw error;
    }
  }

  async currentSession() {
    try {
      const { accessToken, idToken } = (await fetchAuthSession()).tokens ?? {};
      return {
        accessToken: accessToken?.toString(),
        idToken: idToken?.toString(),
        groups: accessToken?.payload['cognito:groups']?.toString(),
      };
    } catch (err) {
      console.error('Error currentSession: ', err);
      throw err;
    }
  }

  async currentAuthenticatedUser() {
    try {
      const { username, userId, signInDetails } = await getCurrentUser();
      return {
        username: username,
        userId: userId,
        signInDetails: signInDetails,
      };
    } catch (err) {
      console.log(err);
      console.error('Error currentUser: ', err);
      throw err;
    }
  }

  async handleFetchUserAttributes() {
    try {
      const userAttributes = await fetchUserAttributes();
      return {
        name: userAttributes.name,
        email: userAttributes.email,
      };
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  //TODO TESTARE
  // modifica un solo attributo utente
  async handleUpdateUserAttribute(attributeKey: string, value: string) {
    try {
      const output = await updateUserAttribute({
        userAttribute: {
          attributeKey,
          value,
        },
      });
      this.handleUpdateUserAttributeNextSteps(output);
    } catch (error) {
      console.log(error);
    }
  }
  //TODO TESTARE
  //secondo passaggio della modifica di un attributo utente
  handleUpdateUserAttributeNextSteps(output: UpdateUserAttributeOutput) {
    const { nextStep } = output;
    switch (nextStep.updateAttributeStep) {
      case 'CONFIRM_ATTRIBUTE_WITH_CODE':
        const codeDeliveryDetails = nextStep.codeDeliveryDetails;
        console.log(
          `Confirmation code was sent to ${codeDeliveryDetails?.deliveryMedium}.`
        );
        // Collect the confirmation code from the user and pass to confirmUserAttribute.
        break;
      case 'DONE':
        console.log(`attribute was successfully updated.`);
        break;
    }
  }
  //TODO TESTARE
  // invia messaggio di verifica per attributo utente
  async handleSendUserAttributeVerificationCode(
    key: VerifiableUserAttributeKey
  ) {
    try {
      await sendUserAttributeVerificationCode({
        userAttributeKey: key,
      });
    } catch (error) {
      console.log(error);
    }
  }
  //TODO TESTARE
  // Conferma attributo con codice di verifica
  async handleConfirmUserAttribute({
    userAttributeKey,
    confirmationCode,
  }: ConfirmUserAttributeInput) {
    try {
      await confirmUserAttribute({ userAttributeKey, confirmationCode });
    } catch (error) {
      console.log(error);
    }
  }
  //TODO TESTARE
  //RESET PASSWORD
  async handleResetPassword(username: string) {
    try {
      const output = await resetPassword({ username });
      this.handleResetPasswordNextSteps(output);
      return true;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
  //TODO TESTARE
  // SEND EMAIL FOR RESET PASSWORD
  handleResetPasswordNextSteps(output: ResetPasswordOutput) {
    const { nextStep } = output;
    switch (nextStep.resetPasswordStep) {
      case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
        const codeDeliveryDetails = nextStep.codeDeliveryDetails;
        console.log(
          `Confirmation code was sent to ${codeDeliveryDetails.deliveryMedium}`
        );
        // Collect the confirmation code from the user and pass to confirmResetPassword.
        break;
      case 'DONE':
        console.log('Successfully reset password.');
        break;
    }
  }
  //TODO TESTARE
  // COMPLETE RESET PASSWORD PROCESS
  async handleConfirmResetPassword({
    username,
    confirmationCode,
    newPassword,
  }: ConfirmResetPasswordInput) {
    try {
      await confirmResetPassword({ username, confirmationCode, newPassword });
      return true;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  async refreshToken() {
    try {
      const { accessToken, idToken } =
        (await fetchAuthSession({ forceRefresh: true })).tokens ?? {};
      return {
        accessToken: accessToken?.toString(),
        idToken: idToken?.toString(),
        groups: accessToken?.payload['cognito:groups'],
      };
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  async handleSignOut() {
    try {
      await signOut();
    } catch (error) {
      console.log('error signing out: ', error);
    }
  }

  handleFirstSignIn = async ({
    name,
    username,
    password,
    newPassword,
  }: {
    name: string;
    username: string;
    password: string;
    newPassword: string;
  }) => {
    let nextStep;

    const { isSignedIn, nextStep: signInNextStep } = await signIn({
      username,
      password,
    });
    nextStep = signInNextStep;

    switch (nextStep.signInStep) {
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        const { missingAttributes } = nextStep;
        const userAttributes = { name };
        const { isSignedIn: confirmIsSignedIn, nextStep: confirmNextStep } =
          await confirmSignIn({
            challengeResponse: newPassword,

            options: {
              userAttributes,
            },
          });
        nextStep = confirmNextStep;
        break;
      case 'DONE':
        break;
    }
  };
}

type SignUpParameters = {
  username: string;
  password: string;
  email: string;
};
